import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArqueoPayment,
  ArqueoTotals,
  ArticleInfo,
  getCustomerName,
  getPaymentTime,
  ARTICLES_CATEGORY_LABEL,
} from "./aggregations";

const fmtMoney = (n: number) =>
  `$${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;

const getCategoryLabel = (p: ArqueoPayment): string => {
  if (p.is_article_sale) return ARTICLES_CATEGORY_LABEL;
  return p.description?.split(",")[0]?.trim() || "—";
};

const getArticleLabel = (
  p: ArqueoPayment,
  articlesById: Map<string, ArticleInfo>
): string => {
  if (!p.is_article_sale) return "—";
  if (p.article_id) {
    const info = articlesById.get(p.article_id);
    if (info) return info.name;
  }
  return p.description || "Artículo";
};

interface ExportContext {
  payments: ArqueoPayment[];
  totals: ArqueoTotals;
  articlesById: Map<string, ArticleInfo>;
  date: Date;
  gymName: string;
  isPartial: boolean;
  isClosed: boolean;
}

export const exportArqueoPdf = ({
  payments,
  totals,
  articlesById,
  date,
  gymName,
  isPartial,
  isClosed,
}: ExportContext) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const dateLabel = format(date, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
  const generatedAt = format(new Date(), "dd/MM/yyyy HH:mm");

  doc.setFontSize(16);
  doc.text("Arqueo de Caja", 40, 40);
  doc.setFontSize(11);
  doc.text(`Gimnasio: ${gymName}`, 40, 60);
  doc.text(`Fecha: ${dateLabel}`, 40, 76);
  doc.text(`Generado: ${generatedAt}`, 40, 92);
  doc.text(`Estado: ${isClosed ? "CERRADO" : "ABIERTO"}`, 40, 108);
  if (isPartial) {
    doc.setTextColor(180, 50, 50);
    doc.text("Vista parcial — los totales reflejan los filtros aplicados.", 40, 124);
    doc.setTextColor(0, 0, 0);
  }

  const startY = isPartial ? 144 : 128;

  autoTable(doc, {
    startY,
    head: [["Cliente", "Método", "Categoría", "Artículo", "Monto", "Hora"]],
    body: payments.map((p) => [
      getCustomerName(p),
      p.payment_method || "—",
      getCategoryLabel(p),
      getArticleLabel(p, articlesById),
      fmtMoney(Number(p.amount) || 0),
      getPaymentTime(p),
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [40, 40, 40] },
  });

  let cursorY = (doc as any).lastAutoTable.finalY + 24;

  doc.setFontSize(13);
  doc.text("Totales", 40, cursorY);
  cursorY += 10;

  autoTable(doc, {
    startY: cursorY,
    head: [["Concepto", "Monto"]],
    body: [
      ["Total general", fmtMoney(totals.total)],
      ["Cantidad de pagos", String(totals.count)],
    ],
    styles: { fontSize: 10 },
  });
  cursorY = (doc as any).lastAutoTable.finalY + 16;

  if (Object.keys(totals.byMethod).length > 0) {
    doc.text("Por método de pago", 40, cursorY);
    cursorY += 10;
    autoTable(doc, {
      startY: cursorY,
      head: [["Método", "Subtotal"]],
      body: Object.entries(totals.byMethod).map(([k, v]) => [k, fmtMoney(v)]),
      styles: { fontSize: 10 },
    });
    cursorY = (doc as any).lastAutoTable.finalY + 16;
  }

  if (Object.keys(totals.byCategory).length > 0) {
    doc.text("Por categoría", 40, cursorY);
    cursorY += 10;
    autoTable(doc, {
      startY: cursorY,
      head: [["Categoría", "Subtotal"]],
      body: Object.entries(totals.byCategory).map(([k, v]) => [k, fmtMoney(v)]),
      styles: { fontSize: 10 },
    });
    cursorY = (doc as any).lastAutoTable.finalY + 16;
  }

  if (totals.articlesBreakdown.length > 0) {
    doc.text("Artículos vendidos", 40, cursorY);
    cursorY += 10;
    autoTable(doc, {
      startY: cursorY,
      head: [["Artículo", "Cantidad", "Subtotal"]],
      body: totals.articlesBreakdown.map((a) => [
        a.name,
        String(a.quantity),
        fmtMoney(a.subtotal),
      ]),
      styles: { fontSize: 10 },
    });
  }

  const safeName = gymName.replace(/[^\w\-]+/g, "_");
  doc.save(`arqueo_${safeName}_${format(date, "yyyy-MM-dd")}.pdf`);
};

export const exportArqueoExcel = ({
  payments,
  totals,
  articlesById,
  date,
  gymName,
  isPartial,
  isClosed,
}: ExportContext) => {
  const wb = XLSX.utils.book_new();

  const meta = [
    ["Arqueo de Caja"],
    ["Gimnasio", gymName],
    ["Fecha", format(date, "yyyy-MM-dd")],
    ["Estado", isClosed ? "CERRADO" : "ABIERTO"],
    ["Vista parcial (filtros activos)", isPartial ? "Sí" : "No"],
    ["Generado", format(new Date(), "yyyy-MM-dd HH:mm")],
  ];

  const pagosSheet = XLSX.utils.aoa_to_sheet([
    ...meta,
    [],
    ["Cliente", "Método", "Categoría", "Artículo", "Monto", "Hora"],
    ...payments.map((p) => [
      getCustomerName(p),
      p.payment_method || "",
      getCategoryLabel(p),
      getArticleLabel(p, articlesById),
      Number(p.amount) || 0,
      getPaymentTime(p),
    ]),
  ]);
  XLSX.utils.book_append_sheet(wb, pagosSheet, "Pagos");

  const totalesSheet = XLSX.utils.aoa_to_sheet([
    ["Total general", totals.total],
    ["Cantidad de pagos", totals.count],
    [],
    ["Por método de pago"],
    ...Object.entries(totals.byMethod).map(([k, v]) => [k, v]),
    [],
    ["Por categoría"],
    ...Object.entries(totals.byCategory).map(([k, v]) => [k, v]),
  ]);
  XLSX.utils.book_append_sheet(wb, totalesSheet, "Totales");

  if (totals.articlesBreakdown.length > 0) {
    const articulosSheet = XLSX.utils.aoa_to_sheet([
      ["Artículo", "Cantidad", "Subtotal"],
      ...totals.articlesBreakdown.map((a) => [a.name, a.quantity, a.subtotal]),
    ]);
    XLSX.utils.book_append_sheet(wb, articulosSheet, "Artículos");
  }

  const safeName = gymName.replace(/[^\w\-]+/g, "_");
  XLSX.writeFile(wb, `arqueo_${safeName}_${format(date, "yyyy-MM-dd")}.xlsx`);
};
